/**
 * Turning a confirmed transaction into spendable notes.
 *
 * The program writes a `CommitmentRecord` PDA for every inserted leaf and
 * emits the insertion as an event; both are read at finality and must agree
 * with what the wallet intended before an output note is given its leaf
 * index. This is shared by the live flows and by journal reconciliation.
 */
import {
  computeSolanaWithdrawBatchHash,
  computeSolanaTokenHash,
  solanaChainDomainId,
  SOLANA_ORCA_WHIRLPOOL_PROGRAM_ID,
  decodeSolanaSwapReceipt,
  decodeSolanaCommitmentRecord,
  extractSolanaPoolEvents,
  getSolanaAccounts,
  solanaPoolAddresses,
  solanaPubkeyHex,
  solanaSettleKindOf,
  type SolanaDepositEvent,
  type SolanaSettlementEvent,
  type SolanaSettlementIntent,
  type SolanaPrivateSwapEvent,
} from "@stealthos/sdk";
import type { JoinSplitNote } from "../../joinsplit/types";
import { fail, requireSafeIndex } from "./common";
import {
  createSolanaFlatNote,
  solanaTreeKey,
  type SolanaNoteNetwork,
} from "./notes";
import { flatFieldHex } from "../notes";
import type { SolanaJournalRecord } from "./journal";
import { getSolanaTransaction, type SolanaRpc } from "./rpc";

export interface SolanaFinalizeNetwork {
  key: string;
  programId: string;
}

function sameWords(left: readonly string[], right: readonly string[]): boolean {
  return (
    left.length === right.length &&
    left.every(
      (value, index) => value.toLowerCase() === right[index].toLowerCase(),
    )
  );
}

async function poolEventsOf(
  rpc: SolanaRpc,
  programId: string,
  signature: string,
) {
  const transaction = await getSolanaTransaction(rpc, signature, "finalized");
  if (!transaction) {
    fail(`finalized transaction ${signature} is not available from the RPC`);
  }
  if (transaction.meta?.err) {
    fail(`transaction ${signature} failed on chain`);
  }
  return {
    slot: transaction.slot,
    events: extractSolanaPoolEvents(transaction, programId).map(
      (occurrence) => occurrence.event,
    ),
  };
}

async function readCommitmentRecords(
  rpc: SolanaRpc,
  programId: string,
  commitments: readonly string[],
): Promise<{ treeIndex: number; leafIndex: number }[]> {
  const addresses = solanaPoolAddresses(programId);
  const { accounts } = await getSolanaAccounts(
    rpc,
    commitments.map((commitment) =>
      addresses.commitmentRecord(commitment as `0x${string}`),
    ),
    "finalized",
  );
  return accounts.map((info, index) => {
    if (!info) fail(`commitment ${commitments[index]} has no record on chain`);
    if (info.owner !== addresses.programId) {
      fail("commitment record is not owned by the pool program");
    }
    const record = decodeSolanaCommitmentRecord(info.data);
    return { treeIndex: record.treeIndex, leafIndex: record.leafIndex };
  });
}

/** Output notes with the leaf positions the program assigned, in commitment order. */
function placeOutputs(
  network: SolanaFinalizeNetwork,
  outputs: readonly JoinSplitNote[],
  records: readonly { treeIndex: number; leafIndex: number }[],
  merkleRoot: string,
): JoinSplitNote[] {
  return outputs.map((note, index) => {
    const record = records[index];
    if (note.treeIndex !== undefined && note.treeIndex !== record.treeIndex) {
      fail(
        "program inserted an output note into a different tree than intended",
      );
    }
    return {
      ...note,
      leafIndex: requireSafeIndex(record.leafIndex, "leafIndex"),
      treeIndex: record.treeIndex,
      treeKey: solanaTreeKey(network.key, network.programId, record.treeIndex),
      merkleRoot,
    };
  });
}

export interface SolanaFinalizedDeposit {
  slot: number;
  note: JoinSplitNote;
  event: SolanaDepositEvent;
}

export async function finalizeSolanaDeposit(
  rpc: SolanaRpc,
  network: SolanaFinalizeNetwork,
  params: { signature: string; note: JoinSplitNote; amount: bigint },
): Promise<SolanaFinalizedDeposit> {
  const { slot, events } = await poolEventsOf(
    rpc,
    network.programId,
    params.signature,
  );
  const commitment = params.note.commitment.toLowerCase();
  const event = events.find(
    (candidate): candidate is SolanaDepositEvent =>
      candidate.name === "Deposit" &&
      candidate.commitment.toLowerCase() === commitment,
  );
  if (!event) fail("transaction carries no deposit event for this note");
  if (event.amount !== params.amount) {
    fail("deposit event amount differs from the intended deposit");
  }
  const [record] = await readCommitmentRecords(rpc, network.programId, [
    commitment,
  ]);
  if (
    record.treeIndex !== event.treeIndex ||
    record.leafIndex !== event.leafIndex
  ) {
    fail("commitment record and deposit event disagree on the leaf position");
  }
  const [note] = placeOutputs(
    network,
    [params.note],
    [record],
    event.merkleRoot,
  );
  return { slot, note, event };
}

export interface SolanaFinalizedSettlement {
  slot: number;
  outputs: JoinSplitNote[];
  event: SolanaSettlementEvent;
}

/** Nullifiers identify spent inputs; the event must also identify this payout. */
export function assertSettlementEventMatchesIntent(
  event: SolanaSettlementEvent,
  intent: SolanaSettlementIntent,
): void {
  const zero = `0x${"00".repeat(32)}`;
  const recipient =
    intent.kind === "withdraw"
      ? solanaPubkeyHex(intent.recipientOwner!, "withdraw recipient")
      : intent.kind === "withdraw_batch"
        ? `0x${computeSolanaWithdrawBatchHash(
            (intent.batch ?? []).map((item) => item.owner),
            (intent.batch ?? []).map((item) => BigInt(item.amount)),
          )
            .toString(16)
            .padStart(64, "0")}`
        : intent.kind === "swap"
          ? solanaPubkeyHex(SOLANA_ORCA_WHIRLPOOL_PROGRAM_ID, "swap adapter")
          : zero;
  const targetTreeIndex =
    intent.commitments.length > 0 || intent.kind === "swap"
      ? (intent.targetTreeIndex ?? intent.sourceTreeIndex)
      : intent.sourceTreeIndex;
  if (
    event.kind !== solanaSettleKindOf(intent.kind) ||
    event.arityId !== intent.arityId ||
    event.sourceTreeIndex !== intent.sourceTreeIndex ||
    event.targetTreeIndex !== targetTreeIndex ||
    solanaPubkeyHex(event.mint, "event mint").toLowerCase() !==
      solanaPubkeyHex(
        intent.mint ?? "11111111111111111111111111111111",
        "intent mint",
      ).toLowerCase() ||
    event.merkleRoot.toLowerCase() !== intent.merkleRoot.toLowerCase() ||
    event.withdrawAmount !== BigInt(intent.withdrawAmount) ||
    event.fee !== BigInt(intent.fee) ||
    event.recipient.toLowerCase() !== recipient.toLowerCase()
  ) {
    fail(
      "finalized settlement event does not match this backed-up payout intent",
    );
  }
}

export async function finalizeSolanaSettlement(
  rpc: SolanaRpc,
  network: SolanaFinalizeNetwork,
  params: {
    signature: string;
    nullifiers: readonly string[];
    commitments: readonly string[];
    outputs: readonly JoinSplitNote[];
    expectedIntent?: SolanaSettlementIntent;
    swapSeed?: SolanaJournalRecord["swapSeed"];
  },
): Promise<SolanaFinalizedSettlement> {
  if (params.expectedIntent?.kind === "swap") {
    // A recovered record may already contain its finalized swap output.
    params = {
      ...params,
      outputs: params.outputs.filter((n) =>
        params.commitments.some(
          (c) => c.toLowerCase() === n.commitment.toLowerCase(),
        ),
      ),
    };
  }
  if (params.outputs.length !== params.commitments.length) {
    fail("output notes and commitments differ in length");
  }
  params.outputs.forEach((note, index) => {
    if (
      note.commitment.toLowerCase() !== params.commitments[index].toLowerCase()
    ) {
      fail("output note order does not match the settlement commitments");
    }
  });
  const { slot, events } = await poolEventsOf(
    rpc,
    network.programId,
    params.signature,
  );
  const event = events.find(
    (candidate): candidate is SolanaSettlementEvent =>
      candidate.name === "Settlement" &&
      sameWords(candidate.nullifiers, params.nullifiers),
  );
  if (!event) fail("transaction carries no settlement event for these notes");
  if (params.expectedIntent?.kind === "swap") {
    const intent = params.expectedIntent;
    const terms = intent.swap!;
    const seed = params.swapSeed;
    if (!seed || seed.outputMint !== terms.outputMint)
      fail("swap recovery seed is missing or mismatched");
    const swaps = events.filter(
      (e): e is SolanaPrivateSwapEvent =>
        e.name === "PrivateSwap" &&
        e.firstNullifier.toLowerCase() === intent.nullifiers[0].toLowerCase(),
    );
    if (swaps.length !== 1)
      fail("transaction must carry one matching swap event");
    const swap = swaps[0];
    const zeroKey = "11111111111111111111111111111111";
    if (
      swap.whirlpool !== terms.whirlpool ||
      swap.inputMint !== (intent.mint ?? zeroKey) ||
      swap.outputMint !== (terms.outputMint ?? zeroKey) ||
      swap.outputNpk.toLowerCase() !== terms.outputNpk.toLowerCase() ||
      swap.inputAmount !== BigInt(intent.withdrawAmount) - BigInt(intent.fee) ||
      swap.outputAmount < BigInt(terms.minOutputAmount) ||
      swap.outputAmount <= 0n ||
      swap.grossOutput !== swap.outputAmount + swap.protocolFee ||
      swap.deadlineSlot !== BigInt(terms.deadlineSlot) ||
      BigInt(slot) > swap.deadlineSlot ||
      swap.treeIndex !== event.targetTreeIndex ||
      swap.leafIndex !== event.firstLeafIndex + params.commitments.length
    )
      fail("finalized swap differs from the backed-up terms");
    const addresses = solanaPoolAddresses(network.programId);
    const {
      accounts: [receiptAccount],
    } = await getSolanaAccounts(
      rpc,
      [addresses.swapReceipt(intent.nullifiers[0])],
      "finalized",
    );
    if (!receiptAccount || receiptAccount.owner !== network.programId)
      fail("swap receipt is missing or has the wrong owner");
    const receipt = decodeSolanaSwapReceipt(receiptAccount.data);
    if (
      receipt.slot !== BigInt(slot) ||
      receipt.treeIndex !== swap.treeIndex ||
      receipt.leafIndex !== swap.leafIndex ||
      receipt.outputCommitment.toLowerCase() !==
        swap.outputCommitment.toLowerCase() ||
      receipt.outputMint !== swap.outputMint ||
      receipt.inputAmount !== swap.inputAmount ||
      receipt.grossOutput !== swap.grossOutput ||
      receipt.protocolFee !== swap.protocolFee ||
      receipt.outputAmount !== swap.outputAmount
    )
      fail("swap receipt and event disagree");
    const output = await createSolanaFlatNote({
      network: {
        key: network.key as SolanaNoteNetwork["key"],
        programId: network.programId,
        chainDomainId: solanaChainDomainId(intent.cluster),
      },
      recipientMpk: seed.recipientMpk,
      random: seed.random,
      mint: terms.outputMint,
      treeIndex: swap.treeIndex,
      tokenHash: computeSolanaTokenHash({
        cluster: intent.cluster,
        programId: network.programId,
        poolConfig: addresses.poolConfig,
        treeIndex: swap.treeIndex,
        mint: terms.outputMint,
      }),
      value: swap.outputAmount,
    });
    if (
      flatFieldHex(output.npk) !== terms.outputNpk.toLowerCase() ||
      output.commitment.toLowerCase() !== swap.outputCommitment.toLowerCase()
    )
      fail("swap output does not belong to the backed-up note seed");
    params = {
      ...params,
      commitments: [...params.commitments, output.commitment],
      outputs: [...params.outputs, output],
    };
  }
  if (!sameWords(event.commitments, params.commitments)) {
    fail("settlement event commitments differ from the intended outputs");
  }
  if (params.expectedIntent) {
    assertSettlementEventMatchesIntent(event, params.expectedIntent);
  }
  if (params.commitments.length === 0) {
    return { slot, outputs: [], event };
  }
  const records = await readCommitmentRecords(
    rpc,
    network.programId,
    params.commitments,
  );
  records.forEach((record, index) => {
    if (
      record.treeIndex !== event.targetTreeIndex ||
      record.leafIndex !== event.firstLeafIndex + index
    ) {
      fail(
        "commitment records and settlement event disagree on leaf positions",
      );
    }
  });
  return {
    slot,
    outputs: placeOutputs(network, params.outputs, records, event.newRoot),
    event,
  };
}

/** Dynamic swap output is delivered to the same encrypted self inbox as change. */
export function settlementDeliveries(
  record: SolanaJournalRecord,
  outputs: readonly JoinSplitNote[],
) {
  const deliveries = [...record.deliveries];
  if (record.kind === "swap" && record.swapSeed) {
    for (const output of outputs) {
      if (
        !deliveries.some(
          (d) => d.commitment.toLowerCase() === output.commitment.toLowerCase(),
        )
      )
        deliveries.push({
          commitment: output.commitment,
          recipientMpkHex: flatFieldHex(record.swapSeed.recipientMpk),
          keepLocal: true,
        });
    }
  }
  return deliveries;
}
